import { useCallback, useMemo } from 'react';

import { 
  CodeType, 
  ItemSearchFilters, 
  SearchCallbacks as UseCodeSearchCallbacks,
  ItemDto
} from 'src/types/base';
import { 
  validateItemSearchFilters,
  validateCustomerSearchFilters,
  validateInspectSearchFilters,
  validateWorkPlaceSearchFilters,
  validateWarehouseSearchFilters
} from 'src/utils/searchHelpers';

// 검색 설정 import
import { getApiConfig } from 'src/utils/categoryUtils';


//모달 기능 훅
export const useCodeSearch = (callbacks?: UseCodeSearchCallbacks) => {
  const { onExactMatch, onNoMatch, onError } = callbacks || {};

  // ===== 성능 최적화: API 설정 캐시 =====
  const apiConfigCache = useMemo(() => new Map<CodeType, any>(), []);

  // ===== 공통 검색 로직 =====
  
  /**
   * 공통 검색 로직을 처리하는 함수
   */
  const executeSearch = useCallback(async <T>(
    type: CodeType,
    searchText: string,
    options: {
      filters?: any;
      exactMatch?: boolean;
      onSuccess?: (item: T) => void;
      autoExactMatch?: boolean;
      validateFilters?: (filters: any) => any;
    } = {}
  ): Promise<T | T[] | null> => {
    const { 
      filters = {}, 
      exactMatch = false, 
      onSuccess, 
      autoExactMatch = false,
      validateFilters 
    } = options;
    
    // 필터 유효성 검사 (필요한 경우)
    const validatedFilters = validateFilters ? validateFilters(filters) : filters;
    
    // 검색 설정 가져오기 (캐시 적용)
    let config = apiConfigCache.get(type);
    if (!config) {
      config = getApiConfig(type);
      if (!config) {
        console.warn(`Unsupported code type: ${type}`);
        return [];
      }
      apiConfigCache.set(type, config);
    }
    
    // 정확한 매칭 모드에서 빈 값 처리
    if (exactMatch && (!searchText || !searchText.toString().trim())) {
      return null;
    }
    
    const inputCd = exactMatch ? searchText.toString().trim().toUpperCase() : searchText;
    
    try {
      // API 파라미터 생성
      const apiParams = config.getApiParams(inputCd, validatedFilters);
      
      // API 호출
      const response = await config.apiFunction(...apiParams);
      let data = response.data.data;
      
      // 응답 데이터 변환 (필요한 경우)
      if (config.transformResponse) {
        data = config.transformResponse(data);
      }
      
      // 정확한 매칭 모드
      if (exactMatch) {
        const foundItem = data.find((item: any) => 
          item[config.codeField]?.trim().toUpperCase() === inputCd
        );
        
        if (foundItem) {
          onSuccess?.(foundItem as T);
          onExactMatch?.(foundItem, type);
          return foundItem as T;
        } else {
          onNoMatch?.(inputCd, type);
          return null;
        }
      }
      
      // 자동 정확한 매칭 처리
      if (autoExactMatch && data.length === 1) {
        const singleItem = data[0];
        const isExactMatch = singleItem[config.codeField]?.trim().toUpperCase() === searchText.toString().trim().toUpperCase();
        
        if (isExactMatch) {
          onSuccess?.(singleItem as T);
          onExactMatch?.(singleItem, type);
          return singleItem as T;
        }
      }
      
      // 일반적인 모달용 검색 (배열 반환)
      return data as T[];
    } catch (error) {
      console.error(`Failed to search ${type}:`, error);
      onError?.(error, type);
      
      if (exactMatch) {
        onNoMatch?.(inputCd, type);
        return null;
      }
      
      return [];
    }
  }, [onExactMatch, onNoMatch, onError, apiConfigCache]);

  /**
   * 공통 모달용 검색 함수
   */
  const searchForModalByType = useCallback(async (
    type: CodeType,
    searchText: string = 'all',
    filters: any = {}
  ) => {
    // 필터 유효성 검사 함수 결정
    let validateFilters: ((filters: any) => any) | undefined;
    if (type === 'item') {
      validateFilters = validateItemSearchFilters;
    } else if (type === 'customer') {
      validateFilters = validateCustomerSearchFilters;
    } else if (type === 'inspect') {
      validateFilters = validateInspectSearchFilters;
    }
    
    const result = await executeSearch(type, searchText, {
      filters,
      validateFilters
    });
    return Array.isArray(result) ? result : [];
  }, [executeSearch]);



  // 공통 테이블용 정확한 검색 함수 (모든 카테고리 지원)
  const searchExactCode = useCallback(async <T = any>(
    type: CodeType,
    code: string,
    onSuccess?: (item: T) => void
  ) => {
    // 필터 유효성 검사 함수 결정
    let validateFilters: ((filters: any) => any) | undefined;
    if (type === 'item') {
      validateFilters = validateItemSearchFilters;
    } else if (type === 'customer') {
      validateFilters = validateCustomerSearchFilters;
    } else if (type === 'inspect') {
      validateFilters = validateInspectSearchFilters;
    } else if (type === 'workplace') {
      validateFilters = validateWorkPlaceSearchFilters;
    } else if (type === 'warehouse') {
      validateFilters = validateWarehouseSearchFilters;
    }

    return await executeSearch<T>(type, code, {
      exactMatch: true,
      onSuccess,
      validateFilters
    });
  }, [executeSearch]);

    // ===== 기존 호환성 함수들 =====

  // 기존 호환성을 위한 품목 전용 래퍼 함수
  const searchExactItemCode = useCallback(async (
    itemCode: string,
    onSuccess?: (item: ItemDto) => void
  ) => {
    return await searchExactCode<ItemDto>('item', itemCode, onSuccess);
  }, [searchExactCode]);

  return {
    // 모달용 검색 (주요 기능)
    searchForModalByType,
    // 공통 정확 검색 (모든 카테고리 지원)
    searchExactCode,
    // 상세 테이블용 정확 검색 (기존 호환성)
    searchExactItemCode // PUR은 일단 이거 사용중 이후 수정 가능
  };
};